/**
 * Organization: Revzoom India Pvt Ltd.
 * User: Sarthak
 * Date: 09-01-2017
 * Time: 10:16 PM
 */
import AbstractMap from './AbstractMap';
import HashMapIterator from './HashMapIterator';

export default class HashMap extends AbstractMap {
  /**
   * @param map{AbstractMap}
   */
  constructor(map = null) {
    super();
    /**
     *
     * @type {{}}
     * @private
     */
    this._map = {};
    /**
     * @type {number}
     * @private
     */
    this._size = 0;

    if (map) {
      this.putAll(map);
    }
  }

  /**
   *
   * @param key
   * @param value
   * @returns {*} Returns old value
   */
  put(key, value) {
    let prevEntry = this.remove(key);
    this._map[key] = { key, value };
    this._size++;
    return prevEntry ? prevEntry.value : undefined;
  }

  /**
   * @param key {string|number}
   * @return {*}
   */
  get(key) {
    const entry = this._map[key];
    return entry ? entry.value : undefined;
  }

  /**
   *
   * @param key
   * @returns {boolean}
   */
  containsKey(key) {
    return this.get(key) !== undefined;
  }

  /**
   *
   * @param itemKey
   * @returns {*} Returns the associated value.
   */
  remove(itemKey) {
    let rv = this.containsKey(itemKey);
    if (rv) {
      let entry = this._map[itemKey];
      delete this._map[itemKey];
      this._size--;
      return entry ? entry.value : null;
    }
    return null;
  }

  /**
   *
   * @param map {AbstractMap}
   */
  putAll(map) {
    const iterator = map.iterator();
    while (iterator.hasNext()) {
      const { key, value } = iterator.next();
      this.put(key, value);
    }
  }

  /**
   *
   * @returns {Array}
   */
  keys() {
    return Object.values(this._map).map((entry) => entry.key);
  }

  /**
   *
   * @return {Array}
   */
  values() {
    return Object.values(this._map).map((entry) => entry.value);
  }

  /**
   *
   */
  clear() {
    /**
     *
     * @type {{}}
     * @private
     */
    this._map = {};
    this._size = 0;
  }

  /**
   *
   * @returns {number}
   */
  size() {
    return this._size;
  }

  /**
   * @return Array Returns an Array of object with key and value properties
   */
  toArray() {
    return Object.values(this._map);
  }

  /**
   *
   * @returns {HashMapIterator}
   */
  iterator() {
    return new HashMapIterator(this);
  }

  /**
   *
   * Callback is called with each key and value as argument.
   * @param {Function} callback
   */
  forEach(callback) {
    if (callback) {
      Object.values(this._map).forEach((entry) => callback(entry.key, entry.value));
    }
  }

  static fromObject(o: Object, hashMap: HashMap = new HashMap()) {
    Object.keys(o).forEach((key) => hashMap.put(key, o[key]));
    return hashMap;
  }

  equals(map: HashMap): boolean {
    if (this === map) {
      return true;
    }
    if (map === undefined || map === null) {
      return false;
    }
    if (this.size() != map.size()) {
      return false;
    }
    const keys: Array = this.keys();
    let key;
    let value;
    for (let i = 0; i < keys.length; i++) {
      key = keys[i];
      value = this.get(key);
      if (value !== map.get(key)) {
        return false;
      }
    }
    return true;
  }

  toMap<KEY, VALUE>(): Map<KEY, VALUE> {
    const map: Map<KEY, VALUE> = new Map();
    this.forEach(function(k, v) {
      map.set(k, v);
    });
    return map;
  }

  static fromMap(map: Map): HashMap {
    const hashMap: HashMap = new HashMap();
    map.forEach(function(v, k) {
      hashMap.put(k, v);
    });
    return hashMap;
  }
}
